import React from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../components/content-box/ContentBox";
import PageActions from "../../../../base/PageActions";
import Breadcrumbs from "../../../../../components/breadcrumbs/Breadcrumbs";
import _image_service from "../../../../../services/ImageService";
import '../../../../styles/Common.scss';
import '../WorldPages.scss';
import '../WorldDynamicContent.scss';

interface BestiaryPageProps {
}

const BestiaryPage = (props: BestiaryPageProps) => {

    const page: PageActions = new PageActions({
        title: `Casarok Bestiary - Chase the Fox`,
        description: "The world of Casarok has a wide variety of creatures from the standard fluffy critters to the powerful elemental dragons!",
        keywords: ["casarok", "bestiary", "fantasy races", "monsters", "anthropomorphic races"],
        imgSrc: `chase/world/bestiary/draconic.png`,
        url: `world/bestiary`
    });

    return page.generateMetaContent(
        <>
            <ContentBox header="Bestiary">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <p>
                        Life on Casarok takes many different forms. The intelligent species are divided into three
                        races, while the rest of sentient life has their own set of three classifications.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={_image_service.getCdnImageSrc(`chase/world/bestiary/anthro.png`)}
                             alt="An Anthro kangaroo"
                             title="An Anthro kangaroo"/>
                    </div>
                    <p className="races-text">
                        The <NavLink className="world-link" to="/world/bestiary/anthro">Anthros</NavLink> are the
                        most common and diverse race of intelligent creatures in Casarok. Most species that make up this
                        race are descendents of various species of animal found on Earth; however, there are some
                        species who evolved in their own way and became native only to Casarok. This race started
                        appearing in Casarok in early to mid Iron Age. Post-cataclysm, this race is defined as those
                        who have a history dating back to this time, are the most flexible but least powerful in their
                        use of magic, and are capable of being primordialized.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={_image_service.getCdnImageSrc(`chase/world/bestiary/aspect.png`)}
                             alt="A Cait Sith, a member of the Aspect race"
                             title="A Cait Sith, a member of the Aspect race"/>
                    </div>
                    <p className="races-text">
                        The <NavLink className="world-link" to="/world/bestiary/aspect">Aspects</NavLink> are the
                        intelligent race of Casarok created to be avatars of
                        the <NavLink className="world-link" to="/world/religion/pantheon">gods and goddesses</NavLink>.
                        Each species of the Aspect race is in the form of a mythical creature that matches their matron
                        or patron deity. Their appearance in Casarok can be traced back to the end of the Draconic Age
                        and into the early Iron Age. In modern times, Aspects are denoted for this point in history,
                        their inability to be primordialized at all, and their affinity for learning spells of
                        elements aligning with their matron or patron deity (though modern Casarokans do not realize
                        this.) They are capable of learning spells outside their affinity, but they take more skill to
                        acquire and are not as powerful as their main elemental spells.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={_image_service.getCdnImageSrc(`chase/world/bestiary/draconic.png`)}
                             alt="A dracamma, one of the six living Draconic species"
                             title="A dracamma, one of the six living Draconic species" />
                    </div>
                    <p className="races-text">
                        The <NavLink className="world-link" to="/world/bestiary/draconic">Draconic</NavLink> race is
                        the least common and diverse of Casarok's three intelligent races. There are only six known
                        species (seven in total throughout history), each one descended from a dfferent species of
                        primordial dragon brought here by the Dragon Deities. They date bac to the Draconic Age and were
                        blessed by the Casarok pantheon with different sets of skills and abilities. The race is defined
                        in the modern day by their species' emergence in the Draconic Age and strictly only being able
                        to learn spells of their designated elements. Additionally, while they are capable of being
                        primordialized, they take on huge forms that act feral when they do (whereas Anthros maintain
                        their normal ability to think.)
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={_image_service.getCdnImageSrc(`chase/world/bestiary/feral.png`)}
                             alt="Tage, a feral creature"
                             title="Tage, a feral creature" />
                    </div>
                    <p className="races-text">
                        <NavLink className="world-link" to="/world/bestiary/feral">Feral</NavLink> is the
                        classification for all non-magical non-intelligent creatures that naturally evolved from the
                        primordial beings of
                        the <NavLink className="world-link" to="/world/history">Creation Era</NavLink>. Many
                        feral species are docile and capable of domestication. Others are hunted for meat, scales, hide,
                        and other resources. Many of them are of the <em>drazon</em> classification, which are
                        creatures aside from the Draconics that evolved from the primordial dragons.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={_image_service.getCdnImageSrc(`chase/world/bestiary/monster.png`)}
                             alt="Humbaba, one of the many dangerous monsters of Casarok"
                             title="Humbaba, one of the many dangerous monsters of Casarok" />
                    </div>
                    <p className="races-text">
                        <NavLink className="world-link" to="/world/bestiary/monster">Monster</NavLink> is the
                        classification for all creatures that evolved from primordial beings through the planet's magic.
                        Most of them are non-intelligent and generally hostile or dangerous. They can be hunted for
                        meat, defense, or sport. In more modern times, magical wards keep monsters out of populated
                        areas.
                    </p>
                    <hr/>
                    <div className="races-image">
                        <img src={_image_service.getCdnImageSrc(`chase/world/bestiary/floradon.png`)}
                             alt="An aimian, one of the more dangerous floradons inhabiting Casarok"
                             title="An aimian, one of the more dangerous floradons inhabiting Casarok" />
                    </div>
                    <p className="races-text">
                        <NavLink className="world-link" to="/world/bestiary/floradon">Floradon</NavLink> is the
                        classification for all highly evolved plant-based life in Casarok. They are the accidental
                        creations of the god of time, Tempatrius, and the goddess of nature, Domathair. All species
                        are magical in nature with some more capable of wielding magic than others.  As such, some
                        species can be domesticated and kept as pets while others are very dangerous. Those floradons
                        that are not poisonous can be hunted for food.
                    </p>
                </div>
            </ContentBox>
        </>
    );
}

export default BestiaryPage;